import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { PROCESSING_LABELS } from '_constants/processing';
import { theme } from '_constants/theme';
import { formatPrice } from '_helpers/common';
import { hexToRGBA } from '_helpers/theme';

import Button from 'components/shared/Button';
import Indicator from 'components/shared/Indicator';
import Text from 'components/shared/layouts/Text';

const StyledContainer = styled.div`
  width: 100%;
  padding: ${theme.spaces.regular}px;
  border-radius: 4px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
`;

const StyledLinkContainer = styled.div`
  margin-bottom: ${theme.spaces.regular}px;

  a {
    font-family: 'RethinkSans';
    font-size: ${theme.fontSizes.regular}px;
    font-weight: 400;
    line-height: 16px;
    color: ${theme.colors.cyanBlue};

    &:hover {
      color: ${theme.colors.cyanBlue};
    }
  }
`;

const StyledPaymentStatusContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin-bottom: ${theme.spaces.regular}px;
`;

const SubscriptionCard = ({
  id,
  orderId,
  productId,
  status,
  productName,
  price,
  nextPayDate,
  orderDate,
  processingOption,
  onUnsubscribe,
}) => (
  <StyledContainer>
    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
      ORDER ID
    </Text>
    <StyledLinkContainer>
      <Link to={`../order/${id}`}>{orderId}</Link>
    </StyledLinkContainer>
    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
      DATE PLACED
    </Text>
    <Text bottomSpace={theme.spaces.regular}>{orderDate}</Text>
    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
      STATUS
    </Text>
    <StyledPaymentStatusContainer>
      <Indicator status={status} />
      <Text>{status}</Text>
    </StyledPaymentStatusContainer>
    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
      MEDICATION
    </Text>
    <Text bottomSpace={theme.spaces.regular}>{productName}</Text>
    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
      PRICE
    </Text>
    <Text bottomSpace={theme.spaces.regular}>{formatPrice(price)}</Text>
    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
      NEXT BILLING DATE
    </Text>
    <Text bottomSpace={theme.spaces.regular}>
      {nextPayDate && !nextPayDate?.includes('null') ? format(parseISO(nextPayDate), 'MMMM dd, yyyy') : '-'}
    </Text>
    <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
      PROCESSING
    </Text>
    <Text bottomSpace={theme.spaces.regular}>{PROCESSING_LABELS[processingOption]}</Text>
    <Button
      small
      outlined
      disabled={status === 'CANCELLED'}
      color={theme.colors.red.primary}
      onClick={onUnsubscribe({
        orderId: id,
        productId: productId,
        productName: productName,
      })}
    >
      Unsubscribe
    </Button>
  </StyledContainer>
);

SubscriptionCard.propTypes = {
  id: PropTypes.string,
  orderId: PropTypes.string,
  productId: PropTypes.string,
  status: PropTypes.string,
  productName: PropTypes.string,
  price: PropTypes.number,
  nextPayDate: PropTypes.string,
  orderDate: PropTypes.string,
  processingOption: PropTypes.string,
  onUnsubscribe: PropTypes.func,
};

export default SubscriptionCard;
